import type { DailyHeadline, DailyHeadlineSubmission, PrivacyState } from "@/local-shared/model";
import { ProfileTab } from "./ProfileTypes";
import NoSubmissionsGraphic from "@/assets/img/layout/profile/no-submissions.webp";

import { shortDateFormat } from "@/util/Util";
import { useContext} from "react";
import { AuthContext } from "@/Realize";
import { motion } from "framer-motion";
import ProfileHeadlines from "./ProfileHeadlines";
import type { ProfileError } from "./Profile";

const SLIDE_IN_TIMING = 0.2;

function ProfileHeadlineSubmission({
    submissionData,
    index,
}: {
    submissionData: DailyHeadlineSubmission;
    index: number;
})
{
    return (
        <motion.div
            className={Wind.style({
                backgroundColor: "bg-white",
                boxShadow: "shadow-md",
                padding: "p-2",
                borderRadius: "rounded-lg",
                display: "flex",
                flexDirection: "flex-col",
                gap: "gap-2",
            }).class}

            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: SLIDE_IN_TIMING, ease: "easeInOut", delay: (SLIDE_IN_TIMING / 2) * index }}
        >
            <div className="relative">
                <img
                    src={submissionData.photo_path}
                    alt={submissionData.description_text}
                    className={Wind.style({
                        width: "w-full",
                        borderRadius: "rounded-lg",
                        objectFit: "object-cover",
                        aspectRatio: "aspect-[173/238]",
                    }).class}
                    loading="lazy"
                />
                <div
                    className="
                        absolute
                        top-0
                        right-0
                        bg-white
                        px-2
                        py-1
                        rounded-bl-lg
                    "
                >
                    {shortDateFormat(new Date(submissionData.created_at).getTime())}
                </div>
            </div>
            {submissionData.description_text}
        </motion.div>
    );
}

export default function ProfileTabRender({
    tab,
    profileId,
    realizations = [],
    security,
    isFollowing,
    showHeadlineCreator,
    showHeadlineCamera,
    showSubmissions,
    refreshProfile,
    setContentError,
}: {
    tab: ProfileTab;
    profileId: number;
    realizations: DailyHeadlineSubmission[]
    security: PrivacyState;
    isFollowing: boolean;
    showHeadlineCreator?: () => void;
    showHeadlineCamera?: (id: number, headline: string) => void;
    showSubmissions?: (headline: DailyHeadline) => void;
    refreshProfile: () => void;
    setContentError: (error: ProfileError | null) => void;
})
{
    const userData = useContext(AuthContext);


    if (!userData)
    {
        return null;
    }

    const userID = parseInt(userData.id, 10) || 0;

    Logger.log(userID, profileId);

    if (userID === 0 || profileId === 0)
    {
        return null;
    }

    switch (tab)
    {
        case ProfileTab.HEADLINES:
            return (
                <ProfileHeadlines
                    userID={userID}
                    profileID={profileId}
                    showHeadlineCreator={showHeadlineCreator}
                    showHeadlineCamera={showHeadlineCamera}
                    showSubmissions={showSubmissions}
                    refreshProfile={refreshProfile}
                    profileSecurity={security}
                    setContentError={setContentError}
                    isFollowing={isFollowing}
                />
            );
        case ProfileTab.REALIZATIONS:

            if (realizations.length === 0)
            {
                return (
                    <motion.div
                        className={Wind.style({
                            width: "w-full",
                            height: "h-full",
                            display: "flex",
                            flexDirection: "flex-col",
                            gap: "gap-3",
                            alignItems: "items-center",
                            justifyContent: "justify-center",
                            color: "text-[#695946]",
                        }).class}

                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                    >
                        <img
                            src={NoSubmissionsGraphic}
                            className={Wind.style({
                                width: "w-60",
                                aspectRatio: "aspect-[240/203]",
                                height: "h-auto",
                                marginX: "mx-auto",
                                objectFit: "object-contain",
                            }).class}
                        />
                        <h1
                            className={Wind.style({
                                fontFamily: "font-poppins",
                                fontSize: "text-xl",
                                fontWeight: "font-bold",
                                lineHeight: "leading-6",
                                letterSpacing: "tracking-[0%]",
                                textAlign: "text-center",
                            }).class}
                        >
                            Nothing yet!
                        </h1>
                        <p
                            className={Wind.style({
                                fontFamily: "font-poppins",
                                fontSize: "text-base",
                                fontWeight: "font-normal",
                                lineHeight: "leading-6",
                                textWrap: "text-balance",
                                textAlign: "text-center",
                                maxWidth: "max-w-64"
                            }).class}
                        >
                            {userID === profileId
                                ? "You haven't submitted any realizations yet!"
                                : "Once they submit realizations they'll appear here"
                            }
                        </p>
                    </motion.div>
                );
            }

            return (
                <motion.div
                    className={Wind.style({
                        display: "grid",
                        gridTemplateColumns: "grid-cols-2",
                        gap: "gap-3",
                        marginTop: "mt-4",
                        paddingX: "px-2",
                        paddingBottom: "pb-4",
                    }).class}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                >
                    {realizations.map((item, index) => (
                        <ProfileHeadlineSubmission
                            key={item.id}
                            submissionData={item}
                            index={index}
                        />
                    )) }
                </motion.div>
            );
    }
}
